import { Link as RouterLink } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { getCompanyOrProfileName } from '@ztechapps/common.utils'
import type { Company, UserProfile } from '@ztechapps/common.contract'
import { Link } from '../Link'

export type DisplayNameProps = {
  company?: Company
  profile?: UserProfile
  linkTo?: string
  useNative?: boolean
}

/** A party's display name — the company name, falling back to the contact profile name — or the `---` empty
 * placeholder when neither resolves. Every widget that shows a party's name renders through here: composing the
 * name inside a template literal is what printed the literal text `undefined` when the party failed to resolve.
 * The placeholder is never wrapped in a link — there is no party to navigate to. Single-span `---` form follows
 * `libs/webui/input/src/InlineInput/index.tsx:243`. `linkTo` is the one link target; `useNative` picks the
 * renderer — the router link by default, a native anchor when set — the same default and spelling as
 * `libs/webui/util/src/DataSuspense/index.tsx:24`. */
export const DisplayName = ({ company, profile, linkTo, useNative = false }: DisplayNameProps) => {
  const name = getCompanyOrProfileName({ company, profile })

  if (isEmpty(name)) {
    return <span>---</span>
  }

  if (isEmpty(linkTo)) {
    return name
  }

  if (useNative) {
    return <Link href={linkTo}>{name}</Link>
  }

  return <RouterLink to={linkTo}>{name}</RouterLink>
}
